import { ArrowRight, Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForegroundPush } from '../../hooks/useForegroundPush';
import { type NotificationItem, useNotifications } from '../../hooks/useNotifications';
import {
  type NotificationTargetRef,
  type PortalApp,
  portalNotificationPath,
  portalNotificationsInboxPath,
} from '../../lib/notification-routes';
import type { PushDriver } from '../../lib/push-setup';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ScrollArea } from '../../ui/scroll-area';
import { NotificationList } from './NotificationList';
import { PushSetupPrompt } from './PushSetupPrompt';

/** Only the newest few fit in a dropdown; the rest are one click away. */
const DROPDOWN_LIMIT = 8;

export interface NotificationBellProps {
  app: PortalApp;
  driver: PushDriver;
  orgId: string | number | null | undefined;
  workspaceId: string | number | null | undefined;
  className?: string;
}

export function NotificationBell({
  app,
  driver,
  orgId,
  workspaceId,
  className,
}: NotificationBellProps) {
  const { notifications, unreadCount, markRead, markAllRead, dismiss } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Mounted here because the bell is present on every workspace route, so this
  // is the one place a portal gets foreground handling without extra wiring.
  useForegroundPush();

  const pathFor = (target: NotificationTargetRef) =>
    portalNotificationPath(app, { ...target, orgId, workspaceId });

  const inboxPath = portalNotificationsInboxPath({ orgId, workspaceId });

  const openNotification = async (notification: NotificationItem) => {
    if (!notification.isRead) await markRead.mutateAsync({ id: notification.id });
    setOpen(false);
    const path = pathFor(notification);
    if (path) navigate(path);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Notifications"
          className={cn(
            'relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground',
            className
          )}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-background bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[340px] overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="rounded px-1 text-xs text-primary hover:bg-muted"
              >
                Mark all read
              </button>
            )}
            {inboxPath && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(inboxPath);
                }}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                title="View all notifications"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Above the list, never instead of it — the inbox works without push. */}
        <PushSetupPrompt driver={driver} />

        <ScrollArea className="max-h-[320px]">
          <NotificationList
            notifications={notifications.slice(0, DROPDOWN_LIMIT)}
            density="compact"
            onOpen={(notification) => void openNotification(notification)}
            onDismiss={(notification) => dismiss.mutate({ id: notification.id })}
          />
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
